import React, { ReactElement, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import MultipleFilesUpload from './MultiFilesUpload'
import { DocumentTypeByIncomeType, IncomeType } from './Options'
import TextInput from '../formGroup/TextInput'
import MySelect from '../formGroup/MySelect'
import { IOption } from '../../interfaces/FormFields'
import { STATUS, STEP2_DOCUMENT_TYPE_FILES } from '../../enums/common'
import {
  getOptionLabelByValue,
  triggerChangeForSelectInput,
} from '../../services/utils'
import { IRevenuCardProps } from '../../interfaces/Step2'
import { ISingleFileUploadProps } from '../../interfaces/Upload'
import { IStore } from '../../interfaces/Store'
import SingleFileUpload from './SingleFileUpload'
import useSingleFileUploadHook from '../../hooks/useSingleFileUploadHook'

const RevenueCard = ({
  register,
  errors,
  index,
  revenue,
  setState,
  state,
  validateFile,
  handleCardChange,
  triggerValidation,
  setMultiFileUpload,
  multiFileUpload,
  revenueCards,
}: IRevenuCardProps): ReactElement => {
  const {
    handleChange,
    uploadFile,
    resetIndexFileState,
    updateState,
  } = useSingleFileUploadHook({
    index,
    setState,
    mainState: state,
    validateFile,
    revenueCards,
    handleCardChange,
    revenue,
  })
  const revenueArray = useSelector(
    (stateStore: IStore) => stateStore.folderReducer.folder.revenue
  )

  useEffect(() => {
    if (
      revenueArray.length > 0 &&
      revenueArray[index] !== undefined &&
      revenueArray[index].revenu_type !== undefined
    ) {
      triggerChangeForSelectInput(
        `revenu_type${index}`,
        revenueArray[index].revenu_type
      )

      if (
        revenueArray[index].document_type !== undefined &&
        DocumentTypeByIncomeType[revenueArray[index].revenu_type].length > 1
      ) {
        triggerChangeForSelectInput(
          `document_type${index}`,
          revenueArray[index].document_type
        )
      }
    }
    // eslint-disable-next-line
  }, [revenueArray])
  const { status, toggleValidateButton, validationButtonError } = state[index]
  const step: number = useSelector(
    (storeState: IStore) => storeState.breadcrumbReducer.step
  )
  const watchRevenueType = revenue.revenu_type || '1'
  const initialDocumentType: IOption[] = DocumentTypeByIncomeType[1]
  const [documentTypes, setDocumentTypes] = useState(initialDocumentType)
  const [documentTypeModeView, setDocumentTypeModeView] = useState(false)
  useEffect(() => {
    if (Number(watchRevenueType) > 0) {
      const docTypes = DocumentTypeByIncomeType[watchRevenueType]
      if (docTypes.length === 1 && watchRevenueType !== '2') {
        setDocumentTypeModeView(true)
        updateState((prevState) => ({
          ...prevState,
          toggleValidateButton: true,
        }))
      } else {
        setDocumentTypeModeView(false)
        resetIndexFileState()
        updateState((prevState) => ({
          ...prevState,
          toggleValidateButton: true,
        }))
      }
      setDocumentTypes(docTypes || [])
    }
    // eslint-disable-next-line
  }, [watchRevenueType, step])
  const error =
    errors && errors.revenue && errors.revenue[revenue.key]
      ? errors.revenue[revenue.key]
      : {
          revenu_type: null,
          document_type: null,
          revenu_montant: null,
        }
  const handleDocumentTypeChange = (e: any) => {
    handleChange(e)
    handleCardChange(index, {
      revenu_type: revenue.revenu_type,
      document_type: e.target.value,
      revenu_montant: revenue.revenu_montant,
      key: revenue.key,
      document_uid: [],
    })
  }
  const showRevenueTypeSelect = status !== STATUS.validated
  let revenueTypeLabel = ''
  if (!showRevenueTypeSelect) {
    revenueTypeLabel = getOptionLabelByValue(IncomeType, watchRevenueType)
  }
  const updateDocumentTypeOnRevenueTypeChange = (revenueType: string) => {
    const [docType] = DocumentTypeByIncomeType[revenueType]
    return docType.value
  }

  const stepFileUploadProps: ISingleFileUploadProps = {
    index,
    title: '',
    setState,
    state,
    validateFile,
    tooltip: 'revenueDocs',
    loadingSpinnerClassName: 'dFlex justifyContentEnd',
    revenueCards,
    handleCardChange,
    revenue,
  }

  const showDocumentTypeSelect = status !== STATUS.validated
  let showDocumentTypeLabel = ''
  if (!showDocumentTypeSelect) {
    showDocumentTypeLabel = getOptionLabelByValue(
      documentTypes,
      Number(revenue.document_type).toString()
    )
  }

  return (
    <div className="row">
      <div className="colXl6">
        <MySelect
          options={IncomeType}
          value={revenue.revenu_type}
          register={register({ required: true })}
          name={`revenue[${revenue.key}][revenu_type]`}
          id={`revenu_type${revenue.key}`}
          error={error.revenu_type}
          label="Type de revenu"
          required
          onChange={(e) => {
            handleCardChange(index, {
              revenu_type: e.target.value,
              document_type: updateDocumentTypeOnRevenueTypeChange(
                e.target.value
              ),
              revenu_montant: revenue.revenu_montant,
              key: revenue.key,
              document_uid: [],
            })
          }}
          className={`${showRevenueTypeSelect ? '' : 'hidden'} mb15 mb45xl`}
        />
        <div
          className={`${
            showRevenueTypeSelect ? 'hidden' : ''
          } inputLabel mb35 wFul`}
        >
          <span className="inputLabelRequired">Type de revenu</span>
          <div className="textPrimary fontSizeSm mt1 fontWeightBold opacity65">
            {revenueTypeLabel}
          </div>
        </div>

        <TextInput
          register={register({
            required: true,
            validate: {
              onlyDigits: (value) =>
                !value.match('[^0-9]') ||
                'Veuillez fournir seulement des chiffres.',
              max: (value) =>
                !(value > 999999999) ||
                'Le montant renseigné dépasse la limite autorisée',
            },
          })}
          error={error.revenu_montant}
          name={`revenue[${revenue.key}][revenu_montant]`}
          id={`revenu_montant${revenue.key}`}
          defaultValue={revenue.revenu_montant}
          label={
            <>
              Montant <strong className="textUppercase">mensuel</strong> net
              imposable (en €)
            </>
          }
          required
          className="mb15 mb0xl"
          inputClassName="inputFieldSecondary"
          placeHolder="Votre montant ici..."
          onChange={(e) =>
            handleCardChange(index, {
              revenu_type: revenue.revenu_type,
              document_type: revenue.document_type,
              revenu_montant: e.target.value,
              key: revenue.key,
              document_uid: revenue.document_uid,
            })
          }
          triggerValidation={triggerValidation}
        />
      </div>

      {watchRevenueType !== '2' && (
        <div className="colXl6">
          {!documentTypeModeView && (
            <div className={`${showDocumentTypeSelect ? '' : 'hidden'}`}>
              <MySelect
                onChange={handleDocumentTypeChange}
                options={documentTypes}
                value={revenue.document_type}
                register={register({ required: true })}
                name={`revenue[${revenue.key}][document_type]`}
                id={`document_type${revenue.key}`}
                error={error.document_type}
                label="Type de document à joindre"
                required
                className="mb15 mb45xl"
              />
            </div>
          )}

          {documentTypeModeView && (
            <div>
              <input
                type="hidden"
                name={`revenue[${revenue.key}][document_type]`}
                id={`document_type${revenue.key}`}
                value={documentTypes[0].value}
                data-testid="revenueDocumentType"
                ref={register}
              />
              <p className="inputLabel">
                <span className="inputLabelRequired">
                  Type de document à joindre
                </span>
              </p>
              <div
                className={`opacity65 ${
                  showDocumentTypeSelect
                    ? 'inputFieldSecondary textSecondaryBlack  pl0 mb15 mb45xl fontStyleNormal fontWeightBold borderNone'
                    : 'textPrimary fontSizeSm fontWeightBold'
                }`}
              >
                {documentTypes[0].label}
              </div>
            </div>
          )}

          {!documentTypeModeView && (
            <div
              className={`${
                showDocumentTypeSelect ? 'hidden' : 'inputLabel mb35'
              }`}
            >
              <span className="inputLabelRequired">
                Type de document à joindre
              </span>
              <div className="textPrimary fontSizeSm mt1 fontWeightBold opacity65">
                {showDocumentTypeLabel}
              </div>
            </div>
          )}

          {documentTypeModeView && (
            <SingleFileUpload {...{ ...stepFileUploadProps }} />
          )}
          {!documentTypeModeView && (
            <div>
              {STEP2_DOCUMENT_TYPE_FILES[Number(revenue.document_type)] &&
                STEP2_DOCUMENT_TYPE_FILES[Number(revenue.document_type)].map(
                  (item, idx) => (
                    <SingleFileUpload
                      {...{
                        ...stepFileUploadProps,
                        title: item,
                        fileIndex: idx,
                      }}
                      key={idx}
                      loadingSpinnerClassName="dFlex justifyContentEnd"
                    />
                  )
                )}
            </div>
          )}
          {toggleValidateButton &&
            revenue.document_type &&
            watchRevenueType !== '2' && (
              <div className="col12 textRight">
                <button
                  className="btn btnPrimary btnPrimaryMd wFull wAutoSm"
                  type="button"
                  onClick={uploadFile}
                >
                  Valider les documents
                </button>
                {validationButtonError && (
                  <p className={`${validationButtonError.type}Error`}>
                    {validationButtonError.message}
                  </p>
                )}
              </div>
            )}
        </div>
      )}

      {watchRevenueType === '2' && (
        <div className="colXl6">
          <input
            type="hidden"
            name={`revenue[${revenue.key}][document_type]`}
            id={`document_type${revenue.key}`}
            value={documentTypes[0].value}
            data-testid="revenueDocumentType"
            ref={register}
          />
          <MultipleFilesUpload
            multiple={true}
            files={multiFileUpload}
            setFiles={setMultiFileUpload}
            index={index}
            revenue={revenue}
            handleCardChange={handleCardChange}
            revenueCards={revenueCards}
          />
        </div>
      )}
    </div>
  )
}

export default RevenueCard
